import { Select, Option } from 'components/Select';
import { range, /* roundByFactor,*/ NO_ELEMENT, rm, setUnitLabel } from 'utils';
import { getQaDataAttr } from '@bygghemmait/utilities';
import { Input, OnChangeHandler, LabeledInput } from 'components/Shared/Form';
import { IQuantity } from 'models/IQuantity';
import styles from './quantity.scss';
import { isMultiQuantity } from 'components/Basket/selector';
import { MultiQuantity as MultiQuantityModel } from 'models/MultiQuantity';
import { Button } from 'components/Button';
import { IconPlus } from 'components/Shared/Icon/themes/BGH-RD3/iconPlus';
import { IconMinus } from 'components/Shared/Icon/themes/BGH-RD3/iconMinus';
import { useDebounce } from 'hooks';
import { useEffect, useState, useRef } from 'react';
import { isNumber } from 'utils';
import { useSelector } from 'react-redux';
import { isMobile as isMobileSelector } from 'components/Shared/responsive';

const FACTOR_PRECISION = 3;

export const calculateMultiQuantity = (quantity: number, factor: number) =>
  +(quantity * factor).toFixed(FACTOR_PRECISION);
export const calculateQuantity = (multiQuantity: number, factor: number) => {
  const amount = multiQuantity / factor;
  return Math.ceil(+amount);
};

const calculateQuantityWithPrecision = (input: number, factor: number): number => {
  const quantity = +(input / factor).toFixed(FACTOR_PRECISION);
  return Math.ceil(quantity);
};
interface OwnProps extends IQuantity {
  updateAmount: (value: number, multiAmount?: number | string, shouldUpdateInput?: boolean) => void;
  multiAmount: number;
  dispatchQuantityEvent: (quantity: number, startQuantity: number) => void;

  /* Experiment Props */
  preferredAmount: number;
  updatePreferredAmount: (value: string | number) => void;
}

const numberRegex = /^\d*[.|,]?\d*$/;

const format = ({ input }: { input: string | number }) => {
  if (isNumber(input)) {
    return input;
  }

  return input.replace(',', '.');
};

const getUnitLabel = (unit: string) => {
  switch (unit) {
    case 'st':
    case 'frp':
      return rm('product_enter_qty', unit);
    default:
      return rm('product_enter_unit', unit);
  }
};

const MultiQuantity = (
  props: MultiQuantityModel &
    Pick<
      OwnProps,
      | 'multiAmount'
      | 'updatePreferredAmount'
      | 'preferredAmount'
      | 'updateAmount'
      | 'amount'
      | 'dispatchQuantityEvent'
    >
) => {
  const startQuantity = useRef(props.amount);
  const handleUpdatePreferred: OnChangeHandler = e => {
    if (numberRegex.exec(`${e.target.value}`)) {
      props.updatePreferredAmount(format({ input: e.target.value }));
    }
  };

  const showCalculatedQuantity =
    props.preferredAmount > 0 && props.amount > 0 && props.multiAmount > 0;
  const updateAmount = (amount: number) => () => {
    const multi = calculateMultiQuantity(amount, props.factor);
    const SHOULD_UPDATE_INPUT = true;
    props.updateAmount(amount, multi, SHOULD_UPDATE_INPUT);
    debounceQuantityGTM(amount);
  };

  const updateInterval = 500;
  const [updateMulti, cancelDebounce] = useDebounce<number | string | boolean | undefined>(
    props.updateAmount,
    updateInterval
  );
  const [debounceQuantityGTM] = useDebounce((quantity: number) => {
    props.dispatchQuantityEvent(quantity, startQuantity.current);
    startQuantity.current = quantity;
  }, updateInterval);

  const onBlur: OnChangeHandler = _e => {
    const amount = calculateQuantityWithPrecision(props.preferredAmount, props.factor);

    props.dispatchQuantityEvent(amount, startQuantity.current);
    startQuantity.current = amount;
  };

  useEffect(() => {
    const amount = calculateQuantityWithPrecision(props.preferredAmount, props.factor);
    const multiAmount = calculateMultiQuantity(amount, props.factor);

    updateMulti(amount, multiAmount);
    return () => {
      cancelDebounce();
    };
  }, [props.preferredAmount, props.factor, updateMulti, cancelDebounce]);

  const onFocus: OnChangeHandler = e => {
    const amount = calculateQuantityWithPrecision(+e.target.value, props.factor);
    startQuantity.current = amount;
  };

  return (
    <div>
      <LabeledInput
        value={props.preferredAmount || ''}
        className={styles.multiInputExperiment}
        onChange={handleUpdatePreferred}
        label={getUnitLabel(props.unit)}
        {...getQaDataAttr(`MultiInput`)}
        inputMode="decimal"
        onBlur={onBlur}
        onFocus={onFocus}
      />
      {showCalculatedQuantity && (
        <div className={styles.calculatedQuantity}>
          <div>
            <p>
              {rm('product_amount_unit', props.amount)}
              <span className={styles.label}>{rm('product_package_amount')}</span>
            </p>
            <p>
              {props.multiAmount} <span dangerouslySetInnerHTML={{ __html: props.unit }} />
              <span className={styles.label}>
                (1 {props.salesUnit} = {props.factor}&nbsp;
                <span dangerouslySetInnerHTML={{ __html: props.unit }} />)
              </span>
            </p>
          </div>
          <div className={styles.multiButtonsWrapper}>
            <Button
              className={styles.multiUpdateButton}
              appearance="tertiary"
              onClick={updateAmount(props.amount - 1)}
            >
              <IconMinus />
            </Button>
            <Button
              className={styles.multiUpdateButton}
              appearance="tertiary"
              onClick={updateAmount(props.amount + 1)}
            >
              <IconPlus />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

const MAX_OPTIONS = 8;
const NormalQuantity = (props: OwnProps) => {
  const RANGE_END = MAX_OPTIONS + props.lowest;
  const [showArbitraryInput, setShowArbitraryInput] = useState(props.lowest > RANGE_END);
  const ARBITRARY_ID = 'arbitrary';
  const arbitraryInputRef = useRef<HTMLInputElement>(NO_ELEMENT);
  const isMobile = useSelector(isMobileSelector);
  const startQuantity = useRef(props.amount);

  useEffect(() => {
    if (showArbitraryInput) {
      const input = arbitraryInputRef.current;
      input?.focus();
      input?.select();
    }
  }, [showArbitraryInput]);

  const onSelectChange = (value: string | number) => {
    if (typeof value === 'number') {
      props.updateAmount(value);
      props.dispatchQuantityEvent(value, startQuantity.current);
      startQuantity.current = value;
    }
    if (value === ARBITRARY_ID) {
      props.updateAmount(RANGE_END + 1);
      props.dispatchQuantityEvent(RANGE_END + 1, startQuantity.current);
      setShowArbitraryInput(true);
      startQuantity.current = RANGE_END + 1;
    }
  };

  const onInputChange: OnChangeHandler = e => {
    props.updateAmount(+e.target.value);
  };

  const onBlur: OnChangeHandler = e => {
    props.dispatchQuantityEvent(+e.target.value, startQuantity.current);
  };
  const onFocus: OnChangeHandler = e => {
    startQuantity.current = +e.target.value;
  };

  return !showArbitraryInput ? (
    <Select
      scrollable
      className={styles.quantity}
      classNames={{
        input: styles.input,
        icon: styles.icon
      }}
      onChange={onSelectChange}
      selectedValue={props.amount}
    >
      {range(props.lowest, RANGE_END).map(amount => (
        <Option
          key={amount}
          label={`${amount} ${setUnitLabel(props.unit, isMobile)}`}
          value={amount}
        />
      ))}
      <Option label={`${RANGE_END + 1}+ ${props.unit}`} value={ARBITRARY_ID} />
    </Select>
  ) : (
    <div className={styles.arbitraryInputWrapper}>
      <Input
        min={props.lowest}
        max={props.highest}
        type="number"
        innerRef={arbitraryInputRef}
        className={styles.quantity}
        value={props.amount}
        onChange={onInputChange}
        inputMode="decimal"
        onBlur={onBlur}
        onFocus={onFocus}
      />
      <span className={styles.arbitraryLabel} dangerouslySetInnerHTML={{ __html: props.unit }} />
    </div>
  );
};
export const Quantity = (props: OwnProps) => {
  if (isMultiQuantity(props)) {
    return <MultiQuantity {...props} />;
  }
  return <NormalQuantity {...props} />;
};
